// =============================================
// serverDocStore — client for the server-side document blob store (/api/dms/blob)
// =============================================
// New uploads push their gzip-base64 bytes to the server blob volume the moment
// browser processing finishes, so the durable copy lives on the SERVER (not just
// in the browser/IndexedDB). The dms_documents row keeps a `blob:` pointer.
//
//   file_path = 'blob:docs/<acct>/<uuid>.b64'   → fetched here via /api/dms/blob
//   file_path = 'https://...'                   → a real remote URL (future Cloud
//                                                 Storage); fetched directly
//   file_path = 'idb://...'                     → local-only fallback (see docStore)
//
// This sits beside src/lib/docStore.ts (the IndexedDB fallback) — docStorage.ts
// chooses server-first, then IndexedDB when offline / unauthenticated.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

/** Is this a key the SERVER (or a remote URL) owns, vs a local idb:// blob? */
export function isServerKey(key: string | null | undefined): boolean {
  return !!key && (key.startsWith('blob:') || key.startsWith('http://') || key.startsWith('https://'));
}

/**
 * Upload a NEW document's gzip-base64 bytes to the server blob store and return
 * its `blob:` key, or null if the server is unavailable / the user isn't logged
 * in (caller then falls back to the local IndexedDB store).
 */
export async function putServerBlob(
  base64: string,
  accountOwnerId?: number | string | null,
): Promise<string | null> {
  const token = authToken();
  if (!token) return null;
  try {
    // Streamed route: the body IS the gzip-base64 text (no JSON wrapper), so a large
    // document is not bound by the server's JSON body limit and never sits in its memory.
    const acct = accountOwnerId != null ? `?account_owner_id=${encodeURIComponent(String(accountOwnerId))}` : '';
    let res = await fetch(`${API_BASE}/dms/blob/raw${acct}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain', Authorization: `Bearer ${token}` },
      body: base64,
    });
    if (res.status === 404) {
      // A backend without the streamed route yet — use the JSON one.
      res = await fetch(`${API_BASE}/dms/blob`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ base64, account_owner_id: accountOwnerId ?? null }),
      });
    }
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.key === 'string' ? json.key : null;
  } catch {
    return null;
  }
}

/** Default per-file cap when the server can't be asked (offline / logged out). */
export const DEFAULT_MAX_UPLOAD_MB = 35;

/**
 * This login's per-file upload cap in MB (plan + per-user override — migration 112).
 * The server enforces the same number; this is only so the screen can say it up front.
 */
export async function getMaxUploadMb(): Promise<number> {
  const token = authToken();
  if (!token) return DEFAULT_MAX_UPLOAD_MB;
  try {
    const res = await fetch(`${API_BASE}/dms/upload-limit`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return DEFAULT_MAX_UPLOAD_MB;
    const mb = Number((await res.json())?.max_upload_mb);
    return Number.isFinite(mb) && mb > 0 ? mb : DEFAULT_MAX_UPLOAD_MB;
  } catch {
    return DEFAULT_MAX_UPLOAD_MB;
  }
}

// =============================================
// Split uploads — how far a file got (migration 120, server/routes/dms-upload.js)
// =============================================

/** One document cut from an uploaded file: the pages of the original it holds (1-based). */
export interface UploadedPiece { from: number; to: number; }

/**
 * Which pages of the file with this SHA-256 are already saved for the account. The
 * uploader skips them, so an interrupted file is finished without saving a page twice.
 * Offline, logged out, or a backend without the route → [] (nothing known; upload all).
 * Throws on a server error or an expired session: guessing "nothing saved" there would
 * duplicate every page already uploaded.
 */
export async function getUploadedPieces(
  sourceHash: string, mode: string, accountOwnerId?: number | string | null,
): Promise<UploadedPiece[]> {
  const token = authToken();
  if (!token) return [];
  const qs = `source_hash=${encodeURIComponent(sourceHash)}&mode=${encodeURIComponent(mode)}`
    + (accountOwnerId != null ? `&account_owner_id=${encodeURIComponent(String(accountOwnerId))}` : '');
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/dms/upload-progress?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    return [];                                   // offline — the outbox path takes over
  }
  if (res.status === 404) return [];             // backend without the route yet
  if (!res.ok) {
    const e = new Error(`Could not check which pages are already uploaded (${res.status}).`);
    (e as any).status = res.status;
    throw e;
  }
  const json = await res.json();
  return Array.isArray(json?.pieces) ? json.pieces : [];
}

/** A file with fewer pages on the server than the original has. */
export interface IncompleteUpload {
  source_hash: string;
  upload_source: string;
  original_filename: string;
  pages_total: number;
  pages_saved: number;
  last_upload: string;
}

/** Files that never fully arrived for this account. Best-effort: [] when it can't be asked. */
export async function getIncompleteUploads(accountOwnerId?: number | string | null): Promise<IncompleteUpload[]> {
  const token = authToken();
  if (!token) return [];
  try {
    const qs = accountOwnerId != null ? `?account_owner_id=${encodeURIComponent(String(accountOwnerId))}` : '';
    const res = await fetch(`${API_BASE}/dms/upload-incomplete${qs}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.files) ? json.files : [];
  } catch {
    return [];
  }
}

/** Fetch a document's gzip-base64 bytes for a server/remote `key`, or null. */
export async function getServerBlob(key: string): Promise<string | null> {
  const token = authToken();
  try {
    // A real remote URL (future Cloud Storage) is fetched directly.
    if (key.startsWith('http://') || key.startsWith('https://')) {
      const res = await fetch(key);
      if (!res.ok) return null;
      return await res.text();
    }
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const qs = `key=${encodeURIComponent(key)}` + (token ? `&token=${encodeURIComponent(token)}` : '');
    // Streamed route first (plain text body); the JSON route is the fallback for a
    // backend that does not have it yet.
    const raw = await fetch(`${API_BASE}/dms/blob/raw?${qs}`, { headers });
    if (raw.ok) return await raw.text();
    const res = await fetch(`${API_BASE}/dms/blob?${qs}`, { headers });
    if (!res.ok) return null;
    const json = await res.json();
    return typeof json?.base64 === 'string' ? json.base64 : null;
  } catch {
    return null;
  }
}

/** Best-effort delete of a server blob (never block a doc delete on this). */
export async function deleteServerBlob(key: string): Promise<void> {
  const token = authToken();
  if (!token || !key.startsWith('blob:')) return;
  try {
    await fetch(`${API_BASE}/dms/blob?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch { /* ignore */ }
}
